import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { Router } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import { flowStyles } from "../styles/flow-styles";
import { loadUserSettings, saveFocusedEntry } from "../utils/entry-flow";
import {
  DEFAULT_SETTINGS,
  EXERCISE_INTENSITY_INFO,
  EXERCISE_TYPE_INFO,
} from "../types";
import type { ExerciseIntensity, ExerciseType, UserSettings } from "../types";

/**
 * Focused flow: log an exercise session (type / intensity / duration). No GL
 * required. See docs/add-task-flows.md.
 */
@customElement("add-exercise-page")
export class AddExercisePage extends LitElement {
  @state() private type: ExerciseType | null = null;
  @state() private intensity: ExerciseIntensity | null = null;
  @state() private duration = "";
  @state() private isSaving = false;
  @state() private showSaved = false;
  @state() private settings: UserSettings = { ...DEFAULT_SETTINGS };

  static styles = [
    flowStyles,
    css`
      .field {
        margin-bottom: 1.25rem;
      }
      .field-label {
        font-size: 0.875rem;
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.5rem;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    void this._load();
  }

  private async _load() {
    this.settings = await loadUserSettings();
  }

  private _handleBack() {
    Router.go("/");
  }

  private async _handleSave() {
    const duration = parseInt(this.duration, 10);
    if (!this.type || !this.intensity || isNaN(duration) || duration <= 0) {
      return;
    }

    this.isSaving = true;
    try {
      await saveFocusedEntry(
        {
          exercise: {
            type: this.type,
            intensity: this.intensity,
            duration,
          },
        },
        this.settings,
      );
      this.showSaved = true;
      setTimeout(() => Router.go("/"), 500);
    } catch (err) {
      console.error("Failed to save exercise:", err);
      this.isSaving = false;
    }
  }

  render() {
    const duration = parseInt(this.duration, 10);
    const canSave =
      !!this.type &&
      !!this.intensity &&
      !isNaN(duration) &&
      duration > 0 &&
      !this.isSaving;

    const effect = this.intensity
      ? EXERCISE_INTENSITY_INFO[this.intensity].bgEffect
      : null;

    return html`
      <div class="page-container">
        ${this.showSaved
          ? html`
              <wa-callout variant="success" open class="success-banner">
                <wa-icon name="circle-check" slot="icon"></wa-icon>
                Exercise saved!
              </wa-callout>
            `
          : nothing}

        <div class="page-header">
          <button class="back-btn" @click=${this._handleBack} aria-label="Back">
            <wa-icon name="arrow-left"></wa-icon>
          </button>
          <h1>Exercise</h1>
        </div>

        <div class="form-section">
          <div class="field">
            <div class="field-label">Activity</div>
            <div class="chip-row">
              ${(Object.keys(EXERCISE_TYPE_INFO) as ExerciseType[]).map(
                (t) => html`
                  <button
                    class="chip ${this.type === t ? "selected" : ""}"
                    @click=${() => (this.type = t)}
                  >
                    <wa-icon name=${EXERCISE_TYPE_INFO[t].icon}></wa-icon>
                    ${EXERCISE_TYPE_INFO[t].label}
                  </button>
                `,
              )}
            </div>
          </div>

          <div class="field">
            <div class="field-label">Intensity</div>
            <div class="chip-row">
              ${(
                Object.keys(EXERCISE_INTENSITY_INFO) as ExerciseIntensity[]
              ).map(
                (i) => html`
                  <button
                    class="chip ${this.intensity === i ? "selected" : ""}"
                    @click=${() => (this.intensity = i)}
                  >
                    ${EXERCISE_INTENSITY_INFO[i].label}
                  </button>
                `,
              )}
            </div>
          </div>

          <div class="field">
            <div class="field-label">Duration</div>
            <wa-input
              class="flow-input"
              type="number"
              inputmode="numeric"
              min="1"
              max="600"
              step="5"
              placeholder="minutes"
              .value=${this.duration}
              @input=${(e: Event) =>
                (this.duration = (e.target as HTMLInputElement).value)}
            ></wa-input>
            <div class="unit-hint">minutes</div>
          </div>

          ${effect
            ? html`
                <wa-callout variant="neutral" open>
                  <wa-icon name="circle-info" slot="icon"></wa-icon>
                  Expected effect on glucose: <strong>${effect}</strong>.
                  Exercise can lower glucose during and for hours afterwards -
                  check before and after.
                </wa-callout>
              `
            : nothing}

          <div class="actions">
            <wa-button
              class="save-btn"
              variant="brand"
              size="large"
              ?disabled=${!canSave}
              ?loading=${this.isSaving}
              @click=${this._handleSave}
            >
              ${this.isSaving ? "Saving..." : "Save exercise"}
            </wa-button>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-exercise-page": AddExercisePage;
  }
}
